import { Component, OnInit, OnDestroy } from '@angular/core';
import * as moment from 'moment';

import {
  EntitiesService,
  TransactionsService,
  PartyRolesService,
  UtilsService,
  NotificationService,
  SessionService,
  NumberFormatService,
} from '../../shared/services';
import { ModalService } from '../../shared/modules/modal/modal.service';

import { Dates, TinyTextEditor } from '../../shared/constants';
import {
  Entity,
  TransactionType,
  PartyRole,
  LegalEntityTransaction,
  Transaction,
  Container,
  TransactionValidationErrorResponse,
  Currency,
  Message,
  TransactionValueEnteredInEnum,
  Country,
  TransactionCurrencyDropdownValue
} from '../../shared/models';
import {
  ClosableModalWithErrorResponseModel
} from '../../shared/components/base/closable-modal-with-error-response-model';

import {
  ConfirmationModalComponent
} from '../../shared/components/confirmation-modal/confirmation-modal.component';
@Component({
  selector: 'transaction-modal',
  templateUrl: './transaction-modal.component.html',
})
export class TransactionModalComponent
  extends ClosableModalWithErrorResponseModel<TransactionValidationErrorResponse>
  implements OnInit, OnDestroy {

  entities: Entity[] = [];
  transactionTypes: TransactionType[] = [];
  partyRoles: PartyRole[] = [];
  transaction: Transaction;
  sourceEntity: Entity = new Entity();
  destinationEntity: Entity = new Entity();
  upeCurrency: Currency;
  upeCurrencyScale: number;
  data: number;
  valueInputTouched = false;
  unitsInputTouched = false;
  descriptionInputTouched = false;
  transferDateYears = [];
  transferDateMonths = [];
  transferDateDays = [];
  taxYear: number;
  workingContainer: Container;
  valueContainsMinus = false;
  unitsContainsMinus = false;
  inputsOldValue = {
    units: '',
    value: '',
  };
  lockedFields: string[] = [];
  transactionCurrencyDropdownValues: TransactionCurrencyDropdownValue[] = [];
  countries: Country[] = [];

  tinyInit = TinyTextEditor.INIT;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _entitiesService: EntitiesService,
    private _transactionsService: TransactionsService,
    private _partyRolesService: PartyRolesService,
    private _utilsService: UtilsService,
    private _sessionService: SessionService,
    private _numberFormatService: NumberFormatService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new TransactionValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit(): void {
    // edit modal doesn't use entities so we prevent the request
    if (!this.data) {
      this.getAllEntities();
    }
    this.getAllTransactionTypes();
    this.getAllPartyRoles();
    this.listenForErrorMessages();

    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.upeCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();
    this.populateTransferDates();

    this.transaction = new Transaction();

    if (this.data) {
      this.setTransactionData(this.data);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => { subscription.unsubscribe(); });
  }

  save(form: HTMLFormElement, closeModal = false): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    const preparedModel = this.prepareModelForSaving();
    if (!this.data && !!form) {
      this.saveNew(preparedModel, closeModal, form);
    } else {
      this.edit(preparedModel);
    }
  }

  close(form = null): void {
    if (form && form.form.touched ||
      (this.unitsInputTouched || this.valueInputTouched || this.descriptionInputTouched)) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Transaction',
          content: 'Are you sure you want to cancel your actions and close it?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  getFormated(num, maxFractionDigits = 1): string {
    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits);
  }

  setOldValue(event, field) {
    this.inputsOldValue[field] = event.target.value;
  }

  formatNumberInput(event, element, field, maxFractionDigits = 1): void {
    const validInputRegex = /^[-]?[0-9,]*[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',');
    const startsWithMinus = firstChar === '-';

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputsOldValue[field];

      return;
    }

    this.valueContainsMinus = field === 'value' ? event.target.value.includes('-') : this.valueContainsMinus;
    this.unitsContainsMinus = field === 'units' ? event.target.value.includes('-') : this.unitsContainsMinus;

    if (field === 'value') {
      this.validationErrors.value = '';
      this.setValueInputTouchedState();
    }

    this.transaction[field] = this._numberFormatService.handleCursorPosition(
      event,
      element,
      this.transaction[field],
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );

    if (field === 'localValue') {
      this.setLocalValueIfNeeded();
    }
  }

  shouldShowValueErrorMessage(): boolean {
    return (!this.valueContainsMinus &&
      (!this.transaction.value && this.valueInputTouched || (+this.transaction.value === 0 && this.valueInputTouched))) ||
      // API returned 422 status with validation error
      (this.validationErrors.value && this.validationErrors.value.location);
  }

  setValueInputTouchedState(touched = true): void {
    this.valueInputTouched = touched;
  }

  setUnitsInputTouchedState(touched = true): void {
    this.unitsInputTouched = touched;
  }

  /**
   * nedded beacuse if user goes directly from description to close
   * it does not recognize that it was touched
   */
  setDescriptionInputTouchedState(touched = true): void {
    this.descriptionInputTouched = touched;
  }

  onMonthChange(): void {
    const month = Number(
      moment().month(this.transaction.transferDateMonth).format('MM')
    );
    const yearAndMonth = `${this.taxYear}-${month}`;

    this.transferDateDays = [];
    for (let day = 1; day < moment(yearAndMonth).daysInMonth() + 1; day++) {
      this.transferDateDays.push(day);
    }
  }

  resetDate(): void {
    this.transferDateDays = [];
    this.transaction.transferDateDay = '';
    this.transaction.transferDateMonth = '';
    this.transaction.transferDateYear = '';
  }

  checkForValidationError(code: string): boolean {
    return !!this.validationErrors[code];
  }

  entitySelected(entityId: number, selectedAs: string): void {
    this.setSelectedAsSourceOrDestinationToFalse(selectedAs);
    const selectedEntity = this.entities.find(entity => entity.entityId === entityId);
    const entity = selectedEntity ? JSON.parse(JSON.stringify(selectedEntity)) : selectedEntity;

    if (entity) {
      entity[selectedAs] = true;
      if (selectedAs === 'isSelectedAsSource') {
        this.sourceEntity = entity;
      } else {
        this.destinationEntity = entity;
      }
    }

    if (this.sourceEntity.entityId && this.destinationEntity.entityId) {
      this.getTransactionCurrencyDropdownValues();
      this.getCountries();
    }

    this.setLocalValueIfNeeded();
  }

  getCurrencyScaleString(currencyScale?: number): string {
    const scale = currencyScale || currencyScale === 0 ? currencyScale : this.upeCurrencyScale;

    return this._utilsService.getCurrencyScaleString(scale);
  }

  isMneType(): boolean {
    return this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.MNE;
  }

  getCurrency(valueEnteredIn: TransactionValueEnteredInEnum): Currency {
    if (valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.countries.find(country => country.isoCode === this.sourceEntity.domicile.isoCode) ? this.countries.find(country => country.isoCode === this.sourceEntity.domicile.isoCode).defaultCurrency : null;
    }

    if (valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.countries.find(country => country.isoCode === this.destinationEntity.domicile.isoCode) ? this.countries.find(country => country.isoCode === this.destinationEntity.domicile.isoCode).defaultCurrency : null;
    }

    return this.upeCurrency;
  }

  getCurrencyIsoCode(): string {
    return this.transactionCurrencyDropdownValues.find(transactionCurrencyDropdownValue => transactionCurrencyDropdownValue.value === this.transaction.valueEnteredIn).currency.isoCode;
  }

  isSourceEntity(entity: Entity): boolean {
    return this.sourceEntity.entityId === entity.entityId;
  }

  isDestinationEntity(entity: Entity): boolean {
    return this.destinationEntity.entityId === entity.entityId;
  }

  getLocalCurrencyScale(): number {
    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.getCurrencyConversionAttribute('localCurrencyScale');
    }

    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.getCurrencyConversionAttribute('localCurrencyScale', this.destinationEntity);
    }
  }

  getLocalCurrencyScaleString(): string {
    const localCurrencyScale = this.getLocalCurrencyScale();

    return this.getCurrencyScaleString(localCurrencyScale);
  }

  shouldShowNoCurrencyConversionErrorMessage(): boolean {
    if (this.transaction.valueEnteredIn !== null && this.transaction.valueEnteredIn !== undefined && !this.isMneType()) {
      return !this.getSourceOrDestinationConversionValue();
    }

    return false;
  }

  setLocalValueIfNeeded(): void {
    if (
      (this.isMneType() && !this.transaction.value) ||
      (!this.isMneType() && !this.transaction.value && !this.transaction.localValue)
    ) {

      // don't calculate anything if there are no values needed for calculations
      return;
    }

    const conversionValue = this.getSourceOrDestinationConversionValue();

    if (!conversionValue && !this.isMneType()) {
      this.transaction.localValue = '';
    }

    if (!this.isMneType() && conversionValue) {
      if (this.shouldCalculateLocalValue()) {
        this.calculateLocalValue(conversionValue);
      } else {
        this.calculateValue(conversionValue);
      }
    } else {
      // set local value to '' because it's hidden in this case, it has to be recalculated for next appearance
      this.transaction.localValue = '';
    }
  }

  private setSelectedAsSourceOrDestinationToFalse(selectedAs: string = null): void {
    this.entities.map(entity => {
      if (selectedAs) {
        entity[selectedAs] = false;
        return false;
      }

      entity.isSelectedAsSource = false;
      entity.isSelectedAsDestination = false;
    });
  }

  private populateTransferDates(): void {
    this.taxYear = this.workingContainer.taxYear;
    this.transferDateYears = [this.taxYear - 1, this.taxYear, this.taxYear + 1];

    this.transferDateMonths = Dates.MONTHS;
  }

  private saveNew(preparedModel: Transaction, closeModal: boolean, form: HTMLFormElement): void {
    this._transactionsService.save(preparedModel).subscribe(() => {
      this.success();
      if (closeModal) {
        this.closeAllModals();
      }
      this.isSaveDisabled = false;
      this.resetForm(form);
    });
  }

  private edit(preparedModel: Transaction): void {
    this._transactionsService.edit(preparedModel.transactionId, preparedModel).subscribe(() => {
      this.success();
      this.closeAllModals();
    });
  }

  private resetForm(form: HTMLFormElement): void {
    this.transaction = new Transaction();
    this.sourceEntity = new Entity();
    this.destinationEntity = new Entity();
    form.reset();
    this.responseErrors = [];
    this.validationErrors = new TransactionValidationErrorResponse();
    this.setValueInputTouchedState(false);
    this.setUnitsInputTouchedState(false);
    this.setDescriptionInputTouchedState(false);
    this.resetDate();
    this.getTransactionCurrencyDropdownValues();
    this.addCurrencyProps();
  }

  private getAllEntities(): void {
    this._entitiesService.list(`?sort=code&order=asc`).subscribe(({ data }) => {
      this.entities = data;
      this.setSelectedAsSourceOrDestinationToFalse();
    });
  }

  private getAllTransactionTypes(): void {
    this._transactionsService.getAllTransactionTypes().subscribe((res) => {
      this.transactionTypes = res.result.transactionTypes;
    });
  }

  private getAllPartyRoles(): void {
    this._partyRolesService.list().subscribe(({ data }) => {
      this.partyRoles = data;
    });
  }

  private setTransactionData(transactionId: number): void {
    this._transactionsService.show(transactionId).subscribe(({ result }) => {
      this.transaction = result.transaction;
      this.lockedFields = result.locked;
      this.setInitialySelectedEntities(result.transaction.legalEntityTransactions);
      this.sourceEntity = this.transaction.legalEntityTransactions.find(legalEntityTransaction => legalEntityTransaction.isSource).entity;
      this.destinationEntity = this.transaction.legalEntityTransactions.find(legalEntityTransaction => !legalEntityTransaction.isSource).entity;

      this.transaction.transferDateMonth = this.transaction.transferDateMonth ?
        moment(`${this.transaction.transferDateMonth}`).format('MMM') : '';

      this.transaction.transferDateDay = this.transaction.transferDateDay || '';

      this.onMonthChange();
      this.getTransactionCurrencyDropdownValues();
      this.getCountries();
      this.setLocalValueIfNeeded();
    });
  }

  private setInitialySelectedEntities(transactions: Transaction[]): void {
    transactions.forEach(transaction => {
      if (transaction.isSource) {
        this.entitySelected(transaction.entity.entityId, 'isSelectedAsSource');
        return;
      }
      this.entitySelected(transaction.entity.entityId, 'isSelectedAsDestination');
    });
  }

  private prepareModelForSaving(): Transaction {
    const model = JSON.parse(JSON.stringify(this.transaction));
    const source = new LegalEntityTransaction();
    const destination = new LegalEntityTransaction();

    source.entity = this.sourceEntity;
    source.isSource = true;
    source.details = '';

    destination.entity = this.destinationEntity;
    destination.isSource = false;
    destination.details = '';

    model.legalEntityTransactions = [source, destination];

    if (model.transferDateMonth !== '') {
      model.transferDateMonth = Number(
        moment().month(model.transferDateMonth).format('MM')
      );
    }

    model.transferDateDay = Number(model.transferDateDay);
    model.transferDateYear = Number(model.transferDateYear);

    if (this.transaction.transferDateMonth === '') {
      model.transferDateMonth = null;
      model.transferDateDay = null;
    }

    if (!this.transaction.transferDateDay) {
      model.transferDateDay = null;
    }

    if (!this.isMneType() && !!model.localValue) {
      model.value = this.getValueBasedOnType(model.localValue);
      delete model.localValue;
    }

    return model;
  }

  private getTransactionCurrencyDropdownValues(): void {
    const transactionValueEnteredInEnum = [];
    for (const [key, value] of Object.entries(TransactionValueEnteredInEnum)) {
      if (!Number.isNaN(+value)) {
        transactionValueEnteredInEnum.push({ value, key });
      }
    }
    this.transactionCurrencyDropdownValues = transactionValueEnteredInEnum;
  }

  private addCurrencyProps(): void {
    this.transactionCurrencyDropdownValues = this.transactionCurrencyDropdownValues.map((currencyType) => {
      currencyType.currency = this.getCurrency(currencyType.value) || new Currency();

      return currencyType;
    }).filter(type => type.value === TransactionValueEnteredInEnum.MNE ||
      type.currency.currencyId !== this.upeCurrency.currencyId);
  }

  private getCountries(): void {
    this.countries = this._sessionService.getCountries();
    this.addCurrencyProps();
  }

  private getSourceOrDestinationConversionValue(): number {
    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Source) {
      return this.getCurrencyConversionAttribute('conversionValue');
    }

    if (this.transaction.valueEnteredIn === TransactionValueEnteredInEnum.Destination) {
      return this.getCurrencyConversionAttribute('conversionValue', this.destinationEntity);
    }
  }

  private getCurrencyConversionAttribute(
    attribute: string,
    sourceOrDestinationEntity: Entity = this.sourceEntity
  ): number {
    const currencyConversion = sourceOrDestinationEntity.legalEntityCurrencyConversion ? sourceOrDestinationEntity.legalEntityCurrencyConversion.find(legalEntityCurrencyConversion => legalEntityCurrencyConversion.taxYear === this.workingContainer.taxYear) : null;
    return currencyConversion ? currencyConversion[attribute] : null;
  }

  private shouldCalculateLocalValue(): boolean {
    return !!this.transaction.value && !this.transaction.localValue;
  }

  private calculateLocalValue(conversionValue: number): void {
    if (this.transaction.localValue === null || this.transaction.localValue === undefined) {
      this.transaction.localValue = '';
      this.transaction.value = 0;
    } else if (conversionValue) {
      const localCurrencyScale = this.getLocalCurrencyScale();
      this.transaction.localValue = this._numberFormatService.getFormatedNumber(
        this._utilsService.calcToLocalCurrency(
          this.transaction.value,
          this.upeCurrencyScale,
          this._numberFormatService.removeCommaAndGetNumber(conversionValue, 1),
          localCurrencyScale
        ),
        6
      );
    } else {
      this.transaction.localValue = '';
    }
  }

  private calculateValue(conversionValue: number): void {
    if (this.shouldShowNoCurrencyConversionErrorMessage()) {
      this.transaction.value = this._numberFormatService.removeCommaAndGetNumber(this.transaction.localValue, 0);
      return;
    }

    if (!this.transaction.localValue || !conversionValue) {
      return;
    }

    const localCurrencyScale = this.getLocalCurrencyScale();

    this.transaction.value = this._utilsService.calcToMneCurrency(
      +this.transaction.localValue,
      this.upeCurrencyScale,
      this._numberFormatService.removeCommaAndGetNumber(conversionValue, 1),
      localCurrencyScale
    );
  }

  private getValueBasedOnType(localValue: string | number): number {
    // TODO consider moving this logic to removeCommaAndGetNumber function
    return typeof localValue === 'string' ?
      this._numberFormatService.removeCommaAndGetNumber(localValue, 0) :
      localValue;
  }
}
